import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  
  const [donationType, setDonationType] = useState('one-time');
  const [selectedAmount, setSelectedAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    mobile: '',
    email: '',
    address: '',
    pan_number: '',
  });

  const [errors, setErrors] = useState({});

  const presetAmounts = [100, 250, 500, 1000, 2500, 5000];

  useEffect(() => {
    if (projectId) {
      const fetchProject = async () => {
        try {
          const response = await axios.get(`${API}/projects/${projectId}`);
          setProject(response.data);
        } catch (error) {
          console.error('Error fetching project:', error);
        }
      };
      fetchProject();
    }
  }, [projectId]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.mobile)) newErrors.mobile = 'Invalid mobile number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.pan_number.trim()) newErrors.pan_number = 'PAN number is required';
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number.toUpperCase())) {
      newErrors.pan_number = 'Invalid PAN format (e.g., ABCDE1234F)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(parseInt(value) || 0);
    }
  };

  const finalAmount = customAmount ? parseInt(customAmount) : selectedAmount;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    if (finalAmount < 1) {
      toast.error('Please select or enter a valid amount');
      return;
    }

    setFormSubmitted(true);
    setLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      const orderResponse = await axios.post(`${API}/donations/create-order`, {
        donor_info: {
          ...formData,
          pan_number: formData.pan_number.toUpperCase(),
        },
        amount: finalAmount,
        donation_type: donationType,
        project_id: projectId || null,
      });

      const { order_id, subscription_id, key_id, donation_id } = orderResponse.data;

      const options = {
        key: key_id,
        amount: finalAmount * 100,
        currency: 'INR',
        name: 'Dharma Astra Association',
        description: project ? `Donation for ${project.title}` : 'Donation to Dharma Astra',
        image: 'https://images.pexels.com/photos/15902534/pexels-photo-15902534.jpeg?auto=compress&cs=tinysrgb&w=100',
        order_id: order_id,
        subscription_id: subscription_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/donations/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              razorpay_subscription_id: response.razorpay_subscription_id,
            });
            setPaymentSuccess(true);
            toast.success('Thank you for your donation!');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.full_name,
          email: formData.email,
          contact: formData.mobile,
        },
        notes: {
          donation_id: donation_id,
          project_id: projectId || 'general',
        },
        theme: {
          color: '#FF9933',
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setLoading(false);

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="payment-success">
        <Card className="max-w-lg w-full bg-white rounded-2xl border-none shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-heading text-3xl text-gray-900 mb-4">Thank You!</h2>
            <p className="font-body text-gray-600 mb-6">
              Thank you for supporting Dharma Astra Association. Your generous contribution helps us build temples, preserve culture, and serve communities.
            </p>
            <p className="font-body text-saffron font-semibold mb-8">
              Donation Amount: ₹{finalAmount.toLocaleString('en-IN')}
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-8 py-3 font-body"
            >
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="donate-page">
      {/* Hero */}
      <section className="relative bg-terracotta py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Support Our Mission</span>
            <h1 className="font-heading text-4xl md:text-5xl text-white mt-4 mb-4">
              Make a Donation
            </h1>
            <p className="font-body text-lg text-white/80">
              {project ? `Donate to: ${project.title}` : 'Your contribution helps us build temples and serve communities.'}
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <div className="space-y-8">
              <img 
                src={project?.image_url || "https://images.pexels.com/photos/15902534/pexels-photo-15902534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                alt="Donation"
                className="w-full h-64 object-cover rounded-2xl shadow-lg"
              />
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading text-xl text-gray-900 mb-4">Why Donate?</h3>
                <ul className="space-y-3 font-body text-sm text-gray-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                    <span>100% of your donation goes to the cause</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                    <span>80G tax exemption certificate provided</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                    <span>Transparent fund utilization reports</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                    <span>Regular updates on project progress</span>
                  </li>
                </ul>
              </div>

              <div className="bg-saffron/10 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                  <p className="font-body text-sm text-gray-700">
                    <strong>Note:</strong> All fields marked are mandatory. PAN card is required for 80G tax exemption certificate.
                  </p>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <Card className="bg-white rounded-2xl border-none shadow-lg">
              <CardContent className="p-8">
                {/* Donation Type Tabs */}
                <Tabs value={donationType} onValueChange={setDonationType} className="mb-8">
                  <TabsList className="w-full bg-gray-100 rounded-full p-1">
                    <TabsTrigger 
                      value="one-time" 
                      className="flex-1 rounded-full py-3 font-body data-[state=active]:bg-saffron data-[state=active]:text-white"
                      data-testid="one-time-tab"
                    >
                      One-time
                    </TabsTrigger>
                    <TabsTrigger 
                      value="monthly" 
                      className="flex-1 rounded-full py-3 font-body data-[state=active]:bg-saffron data-[state=active]:text-white"
                      data-testid="monthly-tab"
                    >
                      Monthly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Amount Selection */}
                <div className="mb-8">
                  <Label className="font-body text-sm font-semibold text-gray-700 mb-4 block">
                    Select Amount
                  </Label>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {presetAmounts.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleAmountSelect(amount)}
                        className={`py-3 px-4 rounded-xl font-body font-semibold transition-all ${
                          selectedAmount === amount && !customAmount
                            ? 'bg-saffron text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`amount-${amount}`}
                      >
                        ₹{amount.toLocaleString('en-IN')}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-body">₹</span>
                    <Input
                      type="text"
                      placeholder="Enter custom amount"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      className="pl-8 py-6 rounded-xl font-body text-lg border-gray-200 focus:border-saffron focus:ring-saffron"
                      data-testid="custom-amount-input"
                    />
                  </div>
                </div>

                {/* Donor Information Form */}
                <div className="space-y-4">
                  <h4 className="font-heading text-lg text-gray-900">Donor Information</h4>
                  
                  <div>
                    <Label className="font-body text-sm text-gray-600">Full Name *</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.full_name ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-full-name"
                    />
                    {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-600">Mobile Number *</Label>
                    <Input
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      className={`mt-1 py-3 rounded-lg font-body ${errors.mobile ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-mobile"
                    />
                    {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-600">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-600">Full Address *</Label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter your complete address"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.address ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-address"
                    />
                    {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-600">PAN Card Number *</Label>
                    <Input
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleInputChange}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      className={`mt-1 py-3 rounded-lg font-body uppercase ${errors.pan_number ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-pan"
                    />
                    {errors.pan_number && <p className="text-red-500 text-xs mt-1">{errors.pan_number}</p>}
                  </div>
                </div>

                {/* Summary & Pay Button */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-body text-gray-600">
                      {donationType === 'monthly' ? 'Monthly Donation' : 'One-time Donation'}
                    </span>
                    <span className="font-heading text-2xl text-saffron">
                      ₹{finalAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <Button
                    onClick={handleProceedToPayment}
                    disabled={loading}
                    className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-full py-6 font-body font-semibold text-lg btn-lift disabled:opacity-50"
                    data-testid="proceed-to-payment-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Heart className="w-5 h-5" />
                        Proceed to Payment
                      </span>
                    )}
                  </Button>
                  <p className="font-body text-xs text-gray-500 text-center mt-4">
                    Secure payment powered by Razorpay
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
